import { isTerminalStatus } from "./types.js";
/** Priority rank — lower sorts first. */
const PRIORITY_RANK = {
    urgent: 0,
    high: 1,
    normal: 2,
    low: 3,
};
/**
 * The ORDERING overdue test — the runtime's, verbatim: a due date strictly
 * before the reference day, regardless of status. Exported for tests and for
 * anyone reasoning about order; for anything a human reads, you want
 * `isOverdueForDisplay`.
 */
export function isOverdueForOrdering(task, todayYmd) {
    return task.dueDate != null && task.dueDate < todayYmd;
}
/**
 * The DISPLAY overdue test — as above but never true for `done`/`dropped`, so a
 * completed task cannot render as overdue. This is the one the panes use for
 * the red due-date label and for the "Vencidas" bucket.
 */
export function isOverdueForDisplay(task, todayYmd) {
    return isOverdueForOrdering(task, todayYmd) && !isTerminalStatus(task.status);
}
/**
 * `true` when due within `[today, soonCutoff]` and not already overdue.
 *
 * Display-only, and deliberately NARROWER than the server's `due:"soon"`
 * filter, which is "dated and not yet overdue" with NO upper bound (the
 * gateway comment says so explicitly: "the pane groups the rest"). The server
 * filters coarsely; the pane draws the horizon.
 */
export function isDueSoon(task, todayYmd, soonCutoff) {
    return (task.dueDate != null &&
        !isOverdueForOrdering(task, todayYmd) &&
        task.dueDate >= todayYmd &&
        task.dueDate <= soonCutoff &&
        !isTerminalStatus(task.status));
}
/**
 * The canonical comparator — the runtime rule.
 *
 * Order:
 *   1. overdue first; among overdue, MOST overdue (earliest due) on top;
 *   2. priority urgent → high → normal → low;
 *   3. dueDate ascending, dated before undated;
 *   4. updatedAt descending (recently-moving work above stale);
 *   5. `id`, so the order is TOTAL and two runs never disagree on ties.
 *
 * Note what is absent: there is no active-before-terminal key. The server does
 * not have one, so neither does this. Panes separate terminal work by GROUPING
 * (see `groupTasks`), which is a display concern, not an ordering one.
 */
export function compareTasks(a, b, todayYmd) {
    // 1. overdue first; among overdue, earliest due first.
    const aOver = isOverdueForOrdering(a, todayYmd);
    const bOver = isOverdueForOrdering(b, todayYmd);
    if (aOver !== bOver)
        return aOver ? -1 : 1;
    if (aOver && bOver && a.dueDate !== b.dueDate) {
        return a.dueDate < b.dueDate ? -1 : 1;
    }
    // 2. priority.
    const p = (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2);
    if (p !== 0)
        return p;
    // 3. dueDate ascending; dated before undated.
    const aDue = a.dueDate ?? null;
    const bDue = b.dueDate ?? null;
    if (aDue !== bDue) {
        if (aDue === null)
            return 1;
        if (bDue === null)
            return -1;
        return aDue < bDue ? -1 : 1;
    }
    // 4. updatedAt descending.
    const au = a.audit.updatedAt;
    const bu = b.audit.updatedAt;
    if (au !== bu)
        return au < bu ? 1 : -1;
    // 5. total-order tiebreak.
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
/** Sort a COPY in canonical order (mirrors the runtime's `sortCanonical`). */
export function sortCanonical(tasks, todayYmd) {
    return [...tasks].sort((a, b) => compareTasks(a, b, todayYmd));
}
/**
 * Group a canonically-ordered list into the pane's sections WITHOUT re-sorting
 * within a group — each bucket preserves incoming order, which is already
 * canonical.
 *
 * Terminal is tested FIRST, which is what keeps the ordering/display split
 * honest: a `done` task with a past due date is "overdue" to the comparator but
 * lands in the terminal tail here, never under "Vencidas".
 */
export function groupTasks(tasks, todayYmd, soonCutoff) {
    const groups = { overdue: [], soon: [], rest: [], terminal: [] };
    for (const t of tasks) {
        if (isTerminalStatus(t.status))
            groups.terminal.push(t);
        else if (isOverdueForDisplay(t, todayYmd))
            groups.overdue.push(t);
        else if (isDueSoon(t, todayYmd, soonCutoff))
            groups.soon.push(t);
        else
            groups.rest.push(t);
    }
    return groups;
}
// ─── reference-day helpers ────────────────────────────────────────────────
//
// ⚠ The two planes do not currently agree on which of these is "today".
// The runtime gateway derives its reference day with `todayYmdUtc(Date.now())`
// (see `gateway-methods/tasklist.ts`), while the panes group against the
// lawyer's LOCAL day. For anyone west of UTC — every pt-BR user — those differ
// for the last hours of the local day, so a task can be ordered overdue by the
// server while the pane groups it as due-today. Both are exported so the choice
// is always explicit at the call site; reconciling them is a runtime-lane
// decision, flagged in the LexPortal handoff.
/** `YYYY-MM-DD` in UTC — the runtime gateway's reference day. */
export function todayYmdUtc(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
/** `YYYY-MM-DD` in the viewer's LOCAL timezone. Uses local getters, never
 *  `toISOString()`, so it never jumps a day for users behind UTC. */
export function localToday(now = new Date()) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
/** `today` + `days`, as a local `YYYY-MM-DD` — the due-soon window end. */
export function localDatePlus(days, now = new Date()) {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    return localToday(d);
}
/** Backwards-compatible alias. The SPAs' old display predicate was named
 *  `isOverdue`; it meant the display flavour, so that is what this is. */
export const isOverdue = isOverdueForDisplay;
//# sourceMappingURL=comparator.js.map